(() => {
  const routes = (window.maisonMarket && window.maisonMarket.routes) || {
    cartAdd: '/cart/add.js',
    cartChange: '/cart/change.js',
    cart: '/cart.js',
    cartUrl: '/cart',
  };

  class CartDrawer {
    constructor(root) {
      this.root = root;
      this.body = root.querySelector('[data-cart-drawer-body]');
      this.trigger = null;
      this.bindOpenTriggers();
      this.bindCloseTriggers();
      this.bindItemControls();
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen()) this.close();
      });
      this.bindProductForms();
    }

    isOpen() {
      return this.root.hasAttribute('data-open');
    }

    open() {
      this.trigger = document.activeElement;
      this.root.setAttribute('data-open', '');
      this.root.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const closeButton = this.root.querySelector('[data-cart-drawer-close]');
      if (closeButton) closeButton.focus();
    }

    close() {
      this.root.removeAttribute('data-open');
      this.root.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.trigger && typeof this.trigger.focus === 'function') this.trigger.focus();
    }

    bindOpenTriggers() {
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          this.open();
        });
      });
    }

    bindCloseTriggers() {
      this.root.querySelectorAll('[data-cart-drawer-close]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        });
      });
    }

    bindProductForms() {
      document.querySelectorAll('[data-product-form]').forEach((form) => {
        if (form.dataset.cartDrawerBound) return;
        form.dataset.cartDrawerBound = 'true';
        form.addEventListener('submit', (event) => this.handleAddToCart(event, form));
      });
    }

    async handleAddToCart(event, form) {
      event.preventDefault();
      const submitButtons = form.id
        ? [...document.querySelectorAll(`[data-add-to-cart][form="${form.id}"]`), ...form.querySelectorAll('[data-add-to-cart]')]
        : [...form.querySelectorAll('[data-add-to-cart]')];
      submitButtons.forEach((button) => { button.disabled = true; });

      try {
        const formData = new FormData(form);
        const response = await fetch(routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data && data.description ? data.description : 'The item could not be added to your bag');
        await this.refresh();
        this.open();
      } catch (error) {
        // Never block a purchase: fall back to a normal page submission if the AJAX call fails.
        form.submit();
        return;
      } finally {
        submitButtons.forEach((button) => { button.disabled = false; });
      }
    }

    bindItemControls() {
      this.root.addEventListener('click', (event) => {
        const item = event.target.closest('[data-cart-drawer-item]');
        if (!item) return;
        const key = item.dataset.key;
        const input = item.querySelector('[data-cart-quantity-input]');

        if (event.target.closest('[data-cart-quantity-minus]')) {
          this.changeQuantity(key, Math.max(0, Number(input.value || 1) - 1), item);
        } else if (event.target.closest('[data-cart-quantity-plus]')) {
          this.changeQuantity(key, Number(input.value || 1) + 1, item);
        } else if (event.target.closest('[data-cart-remove]')) {
          this.changeQuantity(key, 0, item);
        }
      });

      this.root.addEventListener('click', (event) => {
        const upsellButton = event.target.closest('[data-cart-upsell-add]');
        if (!upsellButton) return;
        this.addUpsell(upsellButton.dataset.variantId, upsellButton);
      });

      this.root.addEventListener('change', (event) => {
        if (!event.target.matches('[data-cart-quantity-input]')) return;
        const item = event.target.closest('[data-cart-drawer-item]');
        if (!item) return;
        const quantity = Math.max(0, Number(event.target.value || 0));
        this.changeQuantity(item.dataset.key, quantity, item);
      });
    }

    async changeQuantity(key, quantity, itemEl) {
      if (!key) return;
      itemEl.classList.add('is-updating');
      try {
        const response = await fetch(routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart && cart.description ? cart.description : 'Your bag could not be updated');
        await this.refreshMarkup();
        this.updateCount(cart.item_count);
      } catch (error) {
        itemEl.classList.remove('is-updating');
      }
    }

    async addUpsell(variantId, button) {
      if (!variantId) return;
      button.disabled = true;
      try {
        const response = await fetch(routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data && data.description ? data.description : 'The item could not be added to your bag');
        await this.refreshMarkup();
      } catch (error) {
        button.disabled = false;
      }
    }

    async refresh() {
      await this.refreshMarkup();
    }

    async refreshMarkup() {
      const response = await fetch(`${routes.cartUrl}?section_id=cart-drawer`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const freshRoot = doc.querySelector('[data-cart-drawer]');
      if (!freshRoot) return;
      this.root.innerHTML = freshRoot.innerHTML;
      this.root.dataset.cartItemCount = freshRoot.dataset.cartItemCount || '0';
      this.body = this.root.querySelector('[data-cart-drawer-body]');
      this.bindCloseTriggers();
      this.updateCount(Number(freshRoot.dataset.cartItemCount || 0));
    }

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = String(count);
        el.classList.toggle('is-hidden', count <= 0);
      });
      document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
        el.setAttribute('aria-label', `${el.dataset.cartCountLabel}: ${count}`);
      });
    }
  }

  const drawerRoot = document.querySelector('[data-cart-drawer]');
  if (drawerRoot) new CartDrawer(drawerRoot);
})();
